/**
 * Design token exports — the single source of truth.
 * Imported by Tailwind config + CSS-in-JS + Stories.
 */
import tokens from './tokens.json';

export type ColorTokens = typeof tokens.colors;
export type SpacingTokens = typeof tokens.spacing;
export type RadiusTokens = typeof tokens.radius;

export const colors = tokens.colors;
export const spacing = tokens.spacing;
export const radius = tokens.radius;
export const typography = tokens.typography;
export const motion = tokens.motion;

/**
 * CSS-custom-properties export for non-Tailwind contexts.
 * Paste this in your root layout's globals.css.
 */
export const tokensCssVariables = `
:root {
  /* Brand Primary — Terracotta */
  --brand-50:  ${tokens.colors.brand[50]};
  --brand-100: ${tokens.colors.brand[100]};
  --brand-200: ${tokens.colors.brand[200]};
  --brand-300: ${tokens.colors.brand[300]};
  --brand-400: ${tokens.colors.brand[400]};
  --brand-500: ${tokens.colors.brand[500]};
  --brand-600: ${tokens.colors.brand[600]};
  --brand-700: ${tokens.colors.brand[700]};
  --brand-800: ${tokens.colors.brand[800]};
  --brand-900: ${tokens.colors.brand[900]};

  /* Neutral */
  --neutral-0:   ${tokens.colors.neutral[0]};
  --neutral-50:  ${tokens.colors.neutral[50]};
  --neutral-100: ${tokens.colors.neutral[100]};
  --neutral-200: ${tokens.colors.neutral[200]};
  --neutral-300: ${tokens.colors.neutral[300]};
  --neutral-400: ${tokens.colors.neutral[400]};
  --neutral-500: ${tokens.colors.neutral[500]};
  --neutral-600: ${tokens.colors.neutral[600]};
  --neutral-700: ${tokens.colors.neutral[700]};
  --neutral-800: ${tokens.colors.neutral[800]};
  --neutral-900: ${tokens.colors.neutral[900]};

  /* Semantic */
  --success-100: ${tokens.colors.semantic.success[100]};
  --success-400: ${tokens.colors.semantic.success[400]};
  --success-600: ${tokens.colors.semantic.success[600]};
  --warning-100: ${tokens.colors.semantic.warning[100]};
  --warning-400: ${tokens.colors.semantic.warning[400]};
  --warning-600: ${tokens.colors.semantic.warning[600]};
  --error-100:   ${tokens.colors.semantic.error[100]};
  --error-400:   ${tokens.colors.semantic.error[400]};
  --error-600:   ${tokens.colors.semantic.error[600]};
  --info-100:    ${tokens.colors.semantic.info[100]};
  --info-400:    ${tokens.colors.semantic.info[400]};
  --info-600:    ${tokens.colors.semantic.info[600]};

  /* Surfaces */
  --bg-page:     ${tokens.colors.surface.page};
  --bg-surface:  ${tokens.colors.surface.surface};
  --bg-sunken:   ${tokens.colors.surface.sunken};
  --bg-hover:    ${tokens.colors.surface.hover};
  --bg-active:   ${tokens.colors.surface.active};

  /* Text */
  --text-primary:   ${tokens.colors.text.primary};
  --text-secondary: ${tokens.colors.text.secondary};
  --text-tertiary:  ${tokens.colors.text.tertiary};
  --text-inverse:   ${tokens.colors.text.inverse};
  --text-link:      ${tokens.colors.text.link};

  /* Border */
  --border-subtle:  ${tokens.colors.border.subtle};
  --border-default: ${tokens.colors.border.default};
  --border-strong:  ${tokens.colors.border.strong};
  --border-focus:   ${tokens.colors.border.focus};

  /* Spacing */
  --sp-1: ${tokens.spacing[1]};
  --sp-2: ${tokens.spacing[2]};
  --sp-3: ${tokens.spacing[3]};
  --sp-4: ${tokens.spacing[4]};
  --sp-6: ${tokens.spacing[6]};
  --sp-8: ${tokens.spacing[8]};

  /* Radius */
  --r-md: ${tokens.radius.md};
  --r-lg: ${tokens.radius.lg};
  --r-xl: ${tokens.radius.xl};
  --r-2xl: ${tokens.radius['2xl']};
  --r-pill: ${tokens.radius.pill};

  /* Typography */
  --font-sans: 'Inter', system-ui, sans-serif;
  --font-serif: 'Source Serif Pro', Georgia, serif;
  --font-mono: 'JetBrains Mono', ui-monospace, monospace;

  /* Motion */
  --dur-fast: ${tokens.motion.duration.fast};
  --dur-base: ${tokens.motion.duration.base};
  --dur-slow: ${tokens.motion.duration.slow};
  --dur-slower: ${tokens.motion.duration.slower};
  --ease-out: ${tokens.motion.easing.out};
  --ease-in-out: ${tokens.motion.easing['in-out']};
  --ease-in: ${tokens.motion.easing.in};
}

:root[data-theme="dark"] {
  --bg-page:    #15110D;
  --bg-surface: #221A14;
  --bg-sunken:  #100C09;
  --bg-hover:   #2C221B;
  --bg-active:  #3A2D24;

  --text-primary:   #F5EBE0;
  --text-secondary: #B8A99A;
  --text-tertiary:  #8A7C6F;
  --text-inverse:   #15110D;
  --text-link:      #E89478;

  --border-subtle:  #2C221B;
  --border-default: #3A2D24;
  --border-strong:  #4F3F32;
  --border-focus:   #E89478;

  --brand-400: #E89478;
  --brand-500: #D97757;
}

/* Reduced motion — global reset */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 50ms !important;
  }
}
`;
